/**
 * Workflow system types and interfaces
 */

export interface WorkflowStep {
  id: string;
  name: string;
  type: WorkflowStepType;
  description?: string;
  configuration?: Record<string, unknown>;
  position: {
    x: number;
    y: number;
  };
  inputs?: WorkflowInput[];
  outputs?: WorkflowOutput[];
}

export interface WorkflowInput {
  id: string;
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  required: boolean;
  description?: string;
  defaultValue?: unknown;
}

export interface WorkflowOutput {
  id: string;
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  description?: string;
}

export interface WorkflowConnection {
  id: string;
  sourceStepId: string;
  sourceOutput: string;
  targetStepId: string;
  targetInput: string;
}

export interface WorkflowDefinition {
  id: string;
  name: string;
  description?: string;
  version: string;
  category: WorkflowCategory;
  steps: WorkflowStep[];
  connections: WorkflowConnection[];
  variables?: WorkflowVariable[];
  triggers: WorkflowTrigger[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface WorkflowVariable {
  id: string;
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  value?: unknown;
  description?: string;
}

export interface WorkflowTrigger {
  id: string;
  type: WorkflowTriggerType;
  configuration: Record<string, unknown>;
}

export interface CreateWorkflowRequest {
  name: string;
  description?: string;
  category: WorkflowCategory;
  steps?: WorkflowStep[];
  connections?: WorkflowConnection[];
  variables?: WorkflowVariable[];
  triggers?: WorkflowTrigger[];
}

export interface UpdateWorkflowRequest extends Partial<CreateWorkflowRequest> {
  version?: string;
}

export type WorkflowStepType = 
  | 'start'
  | 'end'
  | 'task'
  | 'decision'
  | 'parallel'
  | 'loop'
  | 'form'
  | 'rule'
  | 'api-call'
  | 'email'
  | 'delay'
  | 'script'
  | 'custom';

export type WorkflowCategory =
  | 'Loan Processing'
  | 'Client Onboarding'
  | 'Document Management'
  | 'Approval Process'
  | 'Notifications'
  | 'Reporting'
  | 'Integration'
  | 'Other';

export type WorkflowTriggerType =
  | 'manual'
  | 'schedule'
  | 'webhook'
  | 'event'
  | 'form-submission'
  | 'rule-execution'
  | 'data-change';

export const WORKFLOW_CATEGORIES: WorkflowCategory[] = [
  'Loan Processing',
  'Client Onboarding', 
  'Document Management',
  'Approval Process',
  'Notifications',
  'Reporting',
  'Integration',
  'Other'
];

export const WORKFLOW_STEP_TYPES: WorkflowStepType[] = [
  'start',
  'end',
  'task',
  'decision',
  'parallel',
  'loop',
  'form',
  'rule', 
  'api-call',
  'email',
  'delay',
  'script',
  'custom'
];

export const WORKFLOW_TRIGGER_TYPES: WorkflowTriggerType[] = [
  'manual',
  'schedule',
  'webhook',
  'event',
  'form-submission',
  'rule-execution',
  'data-change'
];